import React, { forwardRef, useCallback, type Ref } from 'react';
import { ButtonMenuItem, type ButtonMenuItemProps } from '@atlaskit/navigation-system';
import type { MenuItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/utils/get-analytics-attributes/types.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useShallowStableReference } from '@atlassian/jira-utils-stable-reference/src/index.tsx';

export type ButtonMenuItemWithAnalyticsProps = ButtonMenuItemProps & {
	analytics: MenuItemAnalytics;
};

const ButtonMenuItemWithAnalytics = (
	{ analytics, onClick, ...rest }: ButtonMenuItemWithAnalyticsProps,
	ref: Ref<HTMLButtonElement>,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const stableAnalytics = useShallowStableReference(analytics);
	const handleClick = useCallback<NonNullable<ButtonMenuItemWithAnalyticsProps['onClick']>>(
		(event, analyticsEvent) => {
			onClick?.(event, analyticsEvent);

			if (!stableAnalytics) {
				return;
			}

			const { actionSubjectId, ...analyticsAttributes } = stableAnalytics;

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'navigationItem',
				}),
				actionSubjectId,
				analyticsAttributes,
			);
		},
		[stableAnalytics, createAnalyticsEvent, onClick],
	);

	return <ButtonMenuItem ref={ref} onClick={handleClick} {...rest} />;
};

const ButtonMenuItemWithAnalyticsForwarded: React.ForwardRefExoticComponent<
	ButtonMenuItemWithAnalyticsProps & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, ButtonMenuItemWithAnalyticsProps>(ButtonMenuItemWithAnalytics);

export default ButtonMenuItemWithAnalyticsForwarded;
